// Utilidades para manejo de fechas en el componente de seguros

export class DateUtils {
  
  /**
   * Convierte una fecha a formato YYYY-MM-DD
   */
  static toDateString(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Convierte string de fecha a objeto Date
   */
  static fromDateString(dateString: string): Date {
    return new Date(dateString + 'T00:00:00');
  }

  /**
   * Obtiene la fecha de hoy en formato YYYY-MM-DD
   */
  static today(): string {
    return this.toDateString(new Date());
  }

  /**
   * Obtiene una fecha N días atrás
   */
  static daysAgo(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return this.toDateString(date);
  }

  /**
   * Obtiene el primer día del mes actual
   */
  static startOfMonth(): string {
    const date = new Date();
    date.setDate(1);
    return this.toDateString(date);
  }

  /**
   * Obtiene el último día del mes actual
   */
  static endOfMonth(): string {
    const date = new Date();
    date.setMonth(date.getMonth() + 1, 0);
    return this.toDateString(date);
  }

  /**
   * Obtiene el primer día del año actual
   */
  static startOfYear(): string {
    const date = new Date();
    date.setMonth(0, 1);
    return this.toDateString(date);
  }

  /**
   * Obtiene el último día del año actual
   */
  static endOfYear(): string {
    const date = new Date();
    date.setMonth(11, 31);
    return this.toDateString(date);
  }

  /**
   * Formatea una fecha ISO para mostrar en español
   * Usa T00:00:00 para evitar problemas de zona horaria
   */
  static formatSpanishDate(isoString: string): string {
    const date = this.fromDateString(isoString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Formatea fecha y hora en español
   */
  static formatSpanishDateTime(isoString: string): string {
    // Para fechas con hora, usamos el constructor estándar
    const date = new Date(isoString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  /**
   * Calcula la diferencia en días entre dos fechas
   */
  static daysDifference(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Verifica si una fecha está dentro de un rango
   */
  static isDateInRange(date: string, startDate: string, endDate: string): boolean {
    const checkDate = new Date(date);
    const start = new Date(startDate);
    const end = new Date(endDate);
    return checkDate >= start && checkDate <= end;
  }

  /**
   * Obtiene presets de fechas comunes
   */
  static getDatePresets() {
    return {
      today: {
        label: 'Hoy',
        date_from: this.today(),
        date_to: this.today()
      },
      yesterday: {
        label: 'Ayer',
        date_from: this.daysAgo(1),
        date_to: this.daysAgo(1)
      },
      last7Days: {
        label: 'Últimos 7 días',
        date_from: this.daysAgo(7),
        date_to: this.today()
      },
      last30Days: {
        label: 'Últimos 30 días',
        date_from: this.daysAgo(30),
        date_to: this.today()
      },
      thisMonth: {
        label: 'Este mes',
        date_from: this.startOfMonth(),
        date_to: this.endOfMonth()
      },
      thisYear: {
        label: 'Este año',
        date_from: this.startOfYear(),
        date_to: this.endOfYear()
      },
      all: {
        label: 'Todo el tiempo',
        date_from: undefined,
        date_to: undefined
      }
    };
  }

  /**
   * Valida que una fecha esté en formato correcto
   */
  static isValidDateString(dateString: string): boolean {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }

  /**
   * Obtiene el rango de fechas para una comparación (período anterior)
   */
  static getPreviousPeriod(startDate: string, endDate: string): { date_from: string, date_to: string } {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffDays = this.daysDifference(startDate, endDate);
    
    const prevEnd = new Date(start.getTime() - 24 * 60 * 60 * 1000);
    const prevStart = new Date(prevEnd.getTime() - diffDays * 24 * 60 * 60 * 1000);
    
    return {
      date_from: this.toDateString(prevStart),
      date_to: this.toDateString(prevEnd)
    };
  }
}
